/**
 * annotateImage — dependency-free canvas image annotation.
 *
 * PAIRED MODULE — keep in step with the copy in the frontend monorepo:
 * packages/support-panel/src/annotate/ (the client portals' support panel,
 * which lets clients mark up the same screenshots; there it is a TS split —
 * model / render / annotator). Both sides carry the same tools with the same
 * visual output (Box · Arrow · Draw · Highlight · Text · Blur, red / yellow),
 * so an annotation made in one app looks the same when exported from the
 * other. Only the thin React wrappers (AnnotateImage.jsx here) differ per
 * app. Plain ES module, no JSX/TS, no imports — when a tool changes on either
 * side, port it to the other in the same change.
 *
 * Model. Shapes live in SOURCE-IMAGE pixel coordinates; the on-screen canvas is
 * a scaled view (fit to the container, HiDPI-aware) and export re-renders the
 * same shapes onto a canvas of the image's natural size — so "Done" yields a
 * PNG at source resolution regardless of the viewport. Stroke width and font
 * size derive from the image size (stroke_width / font_size) so preview and
 * export match.
 *
 * Tools: rect · arrow · freehand · highlight (translucent marker — box or
 * freehand stroke, HIGHLIGHT_ALPHA with multiply blending, so it tints the
 * pixels underneath instead of covering them) · text · blur (mosaic
 * pixelation — no ctx.filter, works in every browser). Colours: red · yellow;
 * the pen and the marker remember their own colour (marker default yellow).
 * Undo pops the last shape. Esc cancels (a pending text box first). Pointer
 * Events cover mouse + touch + pen.
 *
 * Pure parts (state transitions, geometry, render + export against injected
 * canvas factories) are unit-tested without a real canvas; mount_annotator is
 * the DOM controller.
 */

export const TOOLS = ['rect', 'arrow', 'freehand', 'highlight', 'text', 'blur'];
export const COLORS = { red: '#e53935', yellow: '#fdd835' };
export const DEFAULT_TOOL = 'rect';
export const DEFAULT_COLOR = 'red';
/** Highlight sub-modes: a translucent box, or a freehand marker stroke. */
export const HIGHLIGHT_MODES = ['rect', 'freehand'];
export const DEFAULT_HIGHLIGHT_MODE = 'rect';
export const DEFAULT_HIGHLIGHT_COLOR = 'yellow';
export const HIGHLIGHT_ALPHA = 0.35;
export const HIGHLIGHT_BLEND = 'multiply';

// ── geometry ────────────────────────────────────────────────────────────────

/** Normalise two corners into {x,y,w,h} with non-negative size. */
export const norm_rect = (x0, y0, x1, y1) => ({
    x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0),
});

/** Stroke width in image pixels — scales with the image so marks stay visible. */
export const stroke_width = (w, h) => Math.max(2, Math.round(Math.min(w, h) * 0.006));

/** Text size in image pixels. */
export const font_size = (w, h) => Math.max(14, Math.round(Math.min(w, h) * 0.035));

/** Mosaic block size for the blur tool, in image pixels. */
export const blur_block = (w, h) => Math.max(6, Math.round(Math.min(w, h) / 48));

/** Marker stroke width for freehand highlights, in image pixels (a fat nib). */
export const highlight_width = (w, h) => Math.max(10, Math.round(Math.min(w, h) * 0.025));

/** Display size that fits natural (w,h) inside (max_w,max_h) without upscaling. */
export const fit_size = (natural_w, natural_h, max_w, max_h) => {
    const mw = Math.max(1, max_w || 1);
    const mh = Math.max(1, max_h || 1);
    const scale = Math.min(1, mw / natural_w, mh / natural_h);
    return { width: Math.max(1, Math.round(natural_w * scale)), height: Math.max(1, Math.round(natural_h * scale)), scale };
};

/** Map a client-space point onto image pixels given the canvas' bounding rect; clamped to the image. */
export const to_image_point = (client_x, client_y, rect, natural_w, natural_h) => {
    const sx = rect.width > 0 ? natural_w / rect.width : 1;
    const sy = rect.height > 0 ? natural_h / rect.height : 1;
    const x = (client_x - rect.left) * sx;
    const y = (client_y - rect.top) * sy;
    return { x: Math.min(natural_w, Math.max(0, x)), y: Math.min(natural_h, Math.max(0, y)) };
};

/** Triangle head for an arrow from (x0,y0) to (x1,y1): [tip, left, right]. */
export const arrow_head = (x0, y0, x1, y1, size) => {
    const a = Math.atan2(y1 - y0, x1 - x0);
    const spread = Math.PI / 7;
    return [
        [x1, y1],
        [x1 - size * Math.cos(a - spread), y1 - size * Math.sin(a - spread)],
        [x1 - size * Math.cos(a + spread), y1 - size * Math.sin(a + spread)],
    ];
};

// ── state (immutable transitions) ───────────────────────────────────────────

export const create_state = (natural_w, natural_h, opts) => {
    const o = opts || {};
    const tool = TOOLS.indexOf(o.tool) >= 0 ? o.tool : DEFAULT_TOOL;
    const pen_color = COLORS[o.color] ? o.color : DEFAULT_COLOR;
    const highlight_color = COLORS[o.highlight_color] ? o.highlight_color : DEFAULT_HIGHLIGHT_COLOR;
    return {
        w: natural_w, h: natural_h,
        tool,
        // `color` is what the active tool draws with; the pen and the marker
        // each remember their own so switching tools brings the right one back.
        color: tool === 'highlight' ? highlight_color : pen_color,
        pen_color,
        highlight_color,
        highlight_mode: HIGHLIGHT_MODES.indexOf(o.highlight_mode) >= 0 ? o.highlight_mode : DEFAULT_HIGHLIGHT_MODE,
        shapes: [],
        draft: null,
    };
};

export const set_tool = (state, tool) => {
    if (TOOLS.indexOf(tool) < 0 || tool === state.tool) return state;
    const color = tool === 'highlight' ? state.highlight_color : state.pen_color;
    return { ...state, tool, color, draft: null };
};
export const set_color = (state, color) => {
    if (!COLORS[color] || color === state.color) return state;
    return state.tool === 'highlight'
        ? { ...state, color, highlight_color: color }
        : { ...state, color, pen_color: color };
};
export const set_highlight_mode = (state, mode) =>
    (HIGHLIGHT_MODES.indexOf(mode) >= 0 && mode !== state.highlight_mode ? { ...state, highlight_mode: mode, draft: null } : state);

/** Pointer down: start a draft for the drag tools. Text is committed via commit_text instead. */
export const begin = (state, x, y) => {
    if (state.tool === 'text') return state;
    if (state.tool === 'freehand') return { ...state, draft: { type: 'freehand', color: state.color, points: [[x, y]] } };
    if (state.tool === 'highlight' && state.highlight_mode === 'freehand') {
        return { ...state, draft: { type: 'highlight_stroke', color: state.color, points: [[x, y]] } };
    }
    return { ...state, draft: { type: state.tool, color: state.color, x0: x, y0: y, x1: x, y1: y } };
};

/** Pointer move: extend the draft. */
export const move = (state, x, y) => {
    const d = state.draft;
    if (!d) return state;
    if (Array.isArray(d.points)) {
        const last = d.points[d.points.length - 1];
        if (last && last[0] === x && last[1] === y) return state;
        return { ...state, draft: { ...d, points: d.points.concat([[x, y]]) } };
    }
    return { ...state, draft: { ...d, x1: x, y1: y } };
};

const MIN_DRAG = 3; // image px — anything smaller is a stray tap, not a mark

/** Pointer up: commit the draft unless it is degenerate. */
export const end = (state) => {
    const d = state.draft;
    if (!d) return state;
    const ok = Array.isArray(d.points)
        ? d.points.length >= 2
        : (Math.abs(d.x1 - d.x0) >= MIN_DRAG || Math.abs(d.y1 - d.y0) >= MIN_DRAG);
    return { ...state, draft: null, shapes: ok ? state.shapes.concat([d]) : state.shapes };
};

/** Text tool: place a label at (x,y). Blank text adds nothing. */
export const commit_text = (state, x, y, text) => {
    const t = String(text == null ? '' : text).trim();
    if (!t) return state;
    return { ...state, shapes: state.shapes.concat([{ type: 'text', color: state.color, x, y, text: t }]) };
};

export const can_undo = (state) => !!(state.draft || state.shapes.length);
export const undo = (state) => {
    if (state.draft) return { ...state, draft: null };
    if (!state.shapes.length) return state;
    return { ...state, shapes: state.shapes.slice(0, -1) };
};

// ── rendering (image coordinates; caller applies the view scale) ────────────

const default_env = () => ({
    createCanvas: (w, h) => {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    },
});

const draw_blur = (ctx, shape, image, state, env) => {
    const r = norm_rect(shape.x0, shape.y0, shape.x1, shape.y1);
    if (r.w < 1 || r.h < 1) return;
    const block = blur_block(state.w, state.h);
    const tw = Math.max(1, Math.ceil(r.w / block));
    const th = Math.max(1, Math.ceil(r.h / block));
    const tiny = env.createCanvas(tw, th);
    const tctx = tiny.getContext('2d');
    if (!tctx) return;
    tctx.imageSmoothingEnabled = true;
    tctx.drawImage(image, r.x, r.y, r.w, r.h, 0, 0, tw, th);
    const prev = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tiny, 0, 0, tw, th, r.x, r.y, r.w, r.h);
    ctx.imageSmoothingEnabled = prev;
};

/** Polyline through the points; a lone point still leaves a dot. */
const trace_path = (ctx, pts) => {
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i += 1) ctx.lineTo(pts[i][0], pts[i][1]);
    if (pts.length === 1) ctx.lineTo(pts[0][0] + 0.1, pts[0][1]);
    ctx.stroke();
};

/** Draw one shape. `image` is needed by blur (it samples the source). */
export const draw_shape = (ctx, shape, image, state, env) => {
    const sw = stroke_width(state.w, state.h);
    const color = COLORS[shape.color] || COLORS[DEFAULT_COLOR];
    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = sw;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (shape.type === 'rect') {
        const r = norm_rect(shape.x0, shape.y0, shape.x1, shape.y1);
        ctx.strokeRect(r.x, r.y, r.w, r.h);
    } else if (shape.type === 'arrow') {
        const head = sw * 4;
        const [tip, l, r] = arrow_head(shape.x0, shape.y0, shape.x1, shape.y1, head);
        ctx.beginPath();
        ctx.moveTo(shape.x0, shape.y0);
        ctx.lineTo(shape.x1, shape.y1);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(tip[0], tip[1]);
        ctx.lineTo(l[0], l[1]);
        ctx.lineTo(r[0], r[1]);
        ctx.closePath();
        ctx.fill();
    } else if (shape.type === 'freehand') {
        trace_path(ctx, shape.points);
    } else if (shape.type === 'highlight') {
        // Translucent marker: multiply tints what is underneath instead of covering it.
        const r = norm_rect(shape.x0, shape.y0, shape.x1, shape.y1);
        ctx.globalAlpha = HIGHLIGHT_ALPHA;
        ctx.globalCompositeOperation = HIGHLIGHT_BLEND;
        ctx.fillRect(r.x, r.y, r.w, r.h);
    } else if (shape.type === 'highlight_stroke') {
        ctx.globalAlpha = HIGHLIGHT_ALPHA;
        ctx.globalCompositeOperation = HIGHLIGHT_BLEND;
        ctx.lineWidth = highlight_width(state.w, state.h);
        trace_path(ctx, shape.points);
    } else if (shape.type === 'text') {
        const fs = font_size(state.w, state.h);
        ctx.font = `bold ${fs}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;
        ctx.textBaseline = 'top';
        ctx.lineWidth = Math.max(2, Math.round(fs / 7));
        ctx.strokeStyle = 'rgba(0,0,0,0.85)';
        ctx.strokeText(shape.text, shape.x, shape.y);
        ctx.fillText(shape.text, shape.x, shape.y);
    } else if (shape.type === 'blur') {
        draw_blur(ctx, shape, image, state, env);
    }
    ctx.restore();
};

/** Full render: image at (0,0,w,h), committed shapes, then the draft. */
export const render = (ctx, image, state, env) => {
    const e = env || default_env();
    ctx.drawImage(image, 0, 0, state.w, state.h);
    for (let i = 0; i < state.shapes.length; i += 1) draw_shape(ctx, state.shapes[i], image, state, e);
    if (state.draft) draw_shape(ctx, state.draft, image, state, e);
};

/** Export the annotated image as a PNG Blob at the source resolution. */
export const export_png = (image, state, env) => {
    const e = env || default_env();
    const canvas = e.createCanvas(state.w, state.h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return Promise.reject(new Error('canvas unavailable'));
    render(ctx, image, { ...state, draft: null }, e);
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('export failed'))), 'image/png');
    });
};

/** Suggested filename for the export: "<original stem>-annotated.png". */
export const export_filename = (original) => {
    const stem = String(original || 'image').replace(/\.[a-z0-9]{2,5}$/i, '').trim() || 'image';
    return `${stem}-annotated.png`;
};

// ── DOM controller ──────────────────────────────────────────────────────────

/**
 * Mount an annotation canvas into `container` (must be position:relative and
 * sized; the canvas fits inside it). `image` is a loaded HTMLImageElement.
 * Options: tool, color, highlight_color, highlight_mode, on_change(state),
 * on_cancel(), document, window.
 * Returns a controller: set_tool, set_color, set_highlight_mode, undo,
 * can_undo, get_state, export() → Promise<Blob>, relayout(), destroy().
 */
export function mount_annotator(container, image, opts) {
    const o = opts || {};
    const doc = o.document || document;
    const win = o.window || window;
    const env = o.env || default_env();
    const natural_w = image.naturalWidth || image.width;
    const natural_h = image.naturalHeight || image.height;
    let state = create_state(natural_w, natural_h, o);
    let text_box = null; // pending <input> for the text tool
    let display_scale = 1;
    let destroyed = false;

    const canvas = doc.createElement('canvas');
    canvas.style.touchAction = 'none';
    canvas.style.cursor = 'crosshair';
    canvas.style.display = 'block';
    canvas.style.userSelect = 'none';
    canvas.setAttribute('aria-label', 'Image annotation canvas');
    container.appendChild(canvas);

    const notify = () => { if (o.on_change) o.on_change(state); };

    const draw = () => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const s = canvas.width / natural_w;
        ctx.scale(s, s);
        render(ctx, image, state, env);
    };

    const layout = () => {
        const fit = fit_size(natural_w, natural_h, container.clientWidth, container.clientHeight);
        const dpr = Math.max(1, Math.min(3, win.devicePixelRatio || 1));
        display_scale = fit.scale;
        canvas.width = Math.round(fit.width * dpr);
        canvas.height = Math.round(fit.height * dpr);
        canvas.style.width = `${fit.width}px`;
        canvas.style.height = `${fit.height}px`;
        draw();
    };

    const point_of = (e) => to_image_point(e.clientX, e.clientY, canvas.getBoundingClientRect(), natural_w, natural_h);

    const close_text_box = (commit) => {
        if (!text_box) return;
        const box = text_box;
        text_box = null;
        if (commit) state = commit_text(state, box.ix, box.iy, box.value);
        if (box.parentNode) box.parentNode.removeChild(box);
        draw();
        notify();
    };

    const open_text_box = (p) => {
        close_text_box(true);
        const input = doc.createElement('input');
        input.type = 'text';
        input.placeholder = 'Type, then Enter';
        input.ix = p.x; input.iy = p.y;
        const fs = Math.max(12, Math.round(font_size(natural_w, natural_h) * display_scale));
        // The canvas may sit centred inside the container — offset by its position.
        const left = Math.round((canvas.offsetLeft || 0) + p.x * display_scale);
        const top = Math.round((canvas.offsetTop || 0) + p.y * display_scale);
        input.style.cssText = `position:absolute;left:${left}px;top:${top}px;font:bold ${fs}px system-ui,sans-serif;`
            + `color:${COLORS[state.color]};background:rgba(255,255,255,0.92);border:1px dashed ${COLORS[state.color]};`
            + 'border-radius:3px;padding:1px 4px;min-width:120px;max-width:70%;outline:none;z-index:2;';
        input.addEventListener('keydown', (e) => {
            e.stopPropagation();
            if (e.key === 'Enter') { e.preventDefault(); close_text_box(true); }
            else if (e.key === 'Escape') { e.preventDefault(); close_text_box(false); }
        });
        input.addEventListener('blur', () => { if (text_box === input) close_text_box(true); });
        input.addEventListener('pointerdown', (e) => e.stopPropagation());
        container.appendChild(input);
        text_box = input;
        try { input.focus(); } catch (e) { /* jsdom */ }
    };

    const on_down = (e) => {
        if (destroyed || (e.button != null && e.button !== 0)) return;
        e.preventDefault();
        const p = point_of(e);
        if (state.tool === 'text') { open_text_box(p); return; }
        if (text_box) close_text_box(true);
        state = begin(state, p.x, p.y);
        try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* not supported */ }
        draw();
    };
    const on_move = (e) => {
        if (!state.draft) return;
        e.preventDefault();
        const p = point_of(e);
        state = move(state, p.x, p.y);
        draw();
    };
    const on_up = (e) => {
        if (!state.draft) return;
        e.preventDefault();
        const p = point_of(e);
        state = end(move(state, p.x, p.y));
        try { canvas.releasePointerCapture(e.pointerId); } catch (err) { /* not captured */ }
        draw();
        notify();
    };
    const on_key = (e) => {
        if (e.key === 'Escape') {
            if (text_box) { close_text_box(false); e.stopPropagation(); return; }
            if (o.on_cancel) { e.stopPropagation(); o.on_cancel(); }
        } else if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
            e.preventDefault();
            api.undo();
        }
    };

    canvas.addEventListener('pointerdown', on_down);
    canvas.addEventListener('pointermove', on_move);
    canvas.addEventListener('pointerup', on_up);
    canvas.addEventListener('pointercancel', on_up);
    doc.addEventListener('keydown', on_key, true);
    let ro = null;
    if (typeof win.ResizeObserver === 'function') {
        ro = new win.ResizeObserver(() => layout());
        ro.observe(container);
    } else {
        win.addEventListener('resize', layout);
    }

    const api = {
        canvas,
        get_state: () => state,
        can_undo: () => can_undo(state),
        set_tool: (t) => { if (text_box) close_text_box(true); state = set_tool(state, t); canvas.style.cursor = t === 'text' ? 'text' : 'crosshair'; draw(); notify(); },
        set_color: (c) => { state = set_color(state, c); if (text_box) { text_box.style.color = COLORS[state.color]; text_box.style.borderColor = COLORS[state.color]; } notify(); },
        set_highlight_mode: (m) => { if (text_box) close_text_box(true); state = set_highlight_mode(state, m); draw(); notify(); },
        undo: () => { if (text_box) { close_text_box(false); return; } state = undo(state); draw(); notify(); },
        relayout: layout,
        export: () => { if (text_box) close_text_box(true); return export_png(image, state, env); },
        destroy: () => {
            destroyed = true;
            if (text_box) close_text_box(false);
            canvas.removeEventListener('pointerdown', on_down);
            canvas.removeEventListener('pointermove', on_move);
            canvas.removeEventListener('pointerup', on_up);
            canvas.removeEventListener('pointercancel', on_up);
            doc.removeEventListener('keydown', on_key, true);
            if (ro) ro.disconnect(); else win.removeEventListener('resize', layout);
            if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        },
    };
    layout();
    return api;
}
